import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { FormToggle } from "@/components/form/FormToggle";
import { Icon } from "@/components/icons/Icon";
import { Popover, PopoverButton, PopoverPanel } from "@/components/popover/Popover";
import { Sidebar } from "@/components/sidebar";
import { focusChatInput } from "@/models/chat/chatHelpers";
import { twMerge } from "@/utils/twMerge";

import type { ChatModel } from "@/models/chat/ChatModel";

const ChatInputSettings = observer(function ChatInputSettings({ chat }: { chat: ChatModel }) {
  const t = useTranslations("chat");

  return (
    <Popover className="contents" onClose={() => focusChatInput(chat)}>
      <PopoverButton
        aria-label={t("settings")}
        className={twMerge(
          "group rounded-8",
          "data-[open]:[--color-icon-main:var(--color-icon-active)] data-[open]:[--color-icon-secondary:var(--color-icon-active)]",
        )}
      >
        <Sidebar.Button
          as="div"
          title={t("settings")}
          // The popover button above is the labelled control
          aria-label={undefined}
          icon={<Icon name="Settings" />}
          className={twMerge(
            "w-8 h-8 min-w-8 min-h-8 rounded-8 sm:w-8 sm:h-8 sm:min-w-8 sm:min-h-8",
            "group-data-[open]:bg-sidebar-button-active-bg group-data-[open]:hover:bg-sidebar-button-active-bg",
          )}
          wrapperClassName="p-0"
        />
      </PopoverButton>
      <PopoverPanel
        focus
        data-keep-chat-input-expanded
        anchor={{ to: "top start", padding: 6, gap: 6 }}
        className={twMerge(
          "relative w-[270px] flex flex-col gap-1 p-2.5 rounded-12 bg-block-bg shadow-md dark:shadow-block-inverted z-100",
          "no-scrollbar outline-none",
        )}
      >
        <label className="flex items-center gap-2.5 p-1 h-7.5 rounded-6 cursor-pointer hover:bg-sidebar-button-hover-bg">
          <Icon name="Write" className="w-6 h-6 shrink-0" />
          <div className="flex-1 text-document-text">{t("canEdit")}</div>
          <FormToggle
            asInput
            checked={chat.allowBotWriteToDocument}
            onChange={(e) => chat.setAllowBotWriteToDocument(e.target.checked)}
          />
        </label>
        <div className="text-gray-500 text-size-14 pl-1.5 leading-[1.3]">
          {t("canEditDescription")}
        </div>
      </PopoverPanel>
    </Popover>
  );
});

export default ChatInputSettings;
